'use client';

import React from 'react';
import { SlidersHorizontal, ChevronDown, Search, X } from 'lucide-react';
import type { SortOption } from './ListingsFeedContent';

type Props = {
  resultCount: number;
  sort: SortOption;
  onSortChange: (s: SortOption) => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  activeFilterCount: number;
  onToggleFilters: () => void;
};

const sortOptions: { value: SortOption; label: string }[] = [
  { value: 'newest', label: 'Newest First' },
  { value: 'price-asc', label: 'Price: Low to High' },
  { value: 'price-desc', label: 'Price: High to Low' },
];

export default function FeedHeader({
  resultCount,
  sort,
  onSortChange,
  searchQuery,
  onSearchChange,
  activeFilterCount,
  onToggleFilters,
}: Props) {
  return (
    <div>
      {/* Page title */}
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-white">Browse Listings</h1>
        <p className="text-slate-500 text-sm mt-1">
          Working dog and sport dog equipment from verified UK sellers
        </p>
      </div>

      {/* Controls row */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 w-4 h-4" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search listings..."
            className="w-full bg-[hsl(222,35%,12%)] border border-[hsl(222,25%,18%)] text-slate-100 placeholder-slate-500 rounded-lg pl-9 pr-8 py-2.5 text-sm focus:outline-none focus:border-amber-500/60 focus:ring-1 focus:ring-amber-500/30 transition-all duration-150"
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Mobile filter toggle */}
        <button
          onClick={onToggleFilters}
          className="lg:hidden flex items-center gap-2 bg-[hsl(222,35%,12%)] border border-[hsl(222,25%,18%)] hover:border-slate-500 text-slate-300 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-all duration-150"
        >
          <SlidersHorizontal className="w-4 h-4" />
          Filters
          {activeFilterCount > 0 && (
            <span className="bg-amber-500 text-slate-900 text-xs font-bold w-4 h-4 rounded-full flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </button>

        {/* Result count */}
        <span className="text-sm text-slate-500 tabular-nums ml-auto hidden sm:block">
          <span className="text-white font-semibold">{resultCount.toLocaleString()}</span> listings
        </span>

        {/* Sort */}
        <div className="relative">
          <select
            value={sort}
            onChange={(e) => onSortChange(e.target.value as SortOption)}
            className="appearance-none bg-[hsl(222,35%,12%)] border border-[hsl(222,25%,18%)] text-slate-300 rounded-lg pl-3 pr-8 py-2.5 text-sm focus:outline-none focus:border-amber-500/60 transition-all duration-150 cursor-pointer"
          >
            {sortOptions.map((opt) => (
              <option key={`sort-${opt.value}`} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
        </div>
      </div>
    </div>
  );
}